// PositionTimeline.jsx — one position's points on a time axis (v4 ledger, reference: the Timeline screen of the Elementar Ledger UI
// canvas): value dots on top, distributions above the line, calls below it. Click a point for its source page. Points come from
// GET /api/ledger/timeline's `points` (services/v4_bridge.py): {date, kind: call|distribution|value, amount, doc_hash, filename,
// quote, why, tip, download_url}. A point no page prints (an estimate, a rebuilt flow) is drawn dashed and says why.
import { useMemo, useState } from "react";

const C = { value: "#7A4B00", distribution: "#0F6B5C", call: "#1F4E8C", sel: "#F2C14E", ink: "#15171C", muted: "#555B66", line: "#E2E2DC" };
const KIND_LABEL = { call: "Call", distribution: "Distribution", value: "Value" };
const short = (n) => {
  const a = Math.abs(n);
  return a >= 1e6 ? `${(a / 1e6).toFixed(1)}m` : a >= 1e3 ? `${(a / 1e3).toFixed(1)}k` : a.toFixed(0);
};
const money = (n, ccy) => new Intl.NumberFormat("en-US", {
  style: "currency", currency: /^[A-Z]{3}$/.test(ccy || "") ? ccy : "USD", maximumFractionDigits: 0,
}).format(n);
const ms = (d) => new Date(`${d}T00:00:00Z`).getTime();

export default function PositionTimeline({ points, currency }) {
  const [sel, setSel] = useState(null);
  const geo = useMemo(() => {
    const ts = points.map((p) => ms(p.date));
    const lo = Math.min(...ts), hi = Math.max(...ts);
    const pad = Math.max((hi - lo) * 0.04, 86400000 * 15);
    const x = (d) => ((ms(d) - lo + pad) / (hi - lo + 2 * pad)) * 100;
    const max = (k) => Math.max(1, ...points.filter((p) => p.kind === k).map((p) => p.amount));
    const years = [];
    for (let y = new Date(lo - pad).getUTCFullYear() + 1; y <= new Date(hi + pad).getUTCFullYear(); y += 1) years.push(y);
    return { x, maxV: max("value"), maxD: max("distribution"), maxC: max("call"), years };
  }, [points]);

  if (!points.length) return <p className="px-3 py-4 text-xs text-gray-500">No dated points on this position.</p>;
  const labels = points.length <= 40;   // ponytail: amount labels only when they fit; the panel always has the number

  const mark = (p, i) => {
    const on = sel === i;
    const dashed = !!p.why;
    const left = `${geo.x(p.date)}%`;
    const common = {
      key: i, type: "button", onClick: () => setSel(i),
      title: `${KIND_LABEL[p.kind]} ${money(p.amount, currency)} on ${p.date}`,
      "aria-label": `${KIND_LABEL[p.kind]} ${money(p.amount, currency)} on ${p.date}`,
      "data-testid": `tl-${p.kind}`,
    };
    if (p.kind === "value") {
      const size = p.tip ? 12 : 8;
      return (
        <button {...common} style={{
          position: "absolute", left, top: 10 + (1 - p.amount / geo.maxV) * 130, width: size, height: size, marginLeft: -size / 2,
          borderRadius: "50%", background: dashed ? "#fff" : C.value, border: `2px ${dashed ? "dashed" : "solid"} ${on ? C.sel : C.value}`,
          boxShadow: on ? `0 0 0 3px ${C.sel}` : "none",
        }} />
      );
    }
    const dist = p.kind === "distribution";
    const h = Math.max(4, (p.amount / (dist ? geo.maxD : geo.maxC)) * 64);
    const col = dist ? C.distribution : C.call;
    return (
      <button {...common} style={{
        position: "absolute", left, marginLeft: -5, width: 10, height: h, top: dist ? 240 - h : 280,
        background: dashed ? "transparent" : col, border: `1.5px ${dashed ? "dashed" : "solid"} ${on ? C.sel : col}`,
        boxShadow: on ? `0 0 0 2px ${C.sel}` : "none", borderRadius: 2,
      }}>
        {labels && (
          <span style={{ position: "absolute", left: "50%", transform: "translateX(-50%)", [dist ? "bottom" : "top"]: h + 2,
            fontSize: 10, fontFamily: "IBM Plex Mono, ui-monospace, monospace", color: col, whiteSpace: "nowrap" }}>
            {dist ? "+" : "−"}{short(p.amount)}
          </span>
        )}
      </button>
    );
  };

  const s = sel != null ? points[sel] : null;
  return (
    <div className="flex flex-wrap gap-3" data-testid="position-timeline">
      <div className="flex-1 min-w-0 overflow-x-auto">
        <div style={{ position: "relative", height: 380, minWidth: 480 }}>
          <span style={{ position: "absolute", left: 4, top: 0, fontSize: 10, color: C.muted }}>VALUE</span>
          <span style={{ position: "absolute", left: 4, top: 160, fontSize: 10, color: C.muted }}>DISTRIBUTIONS</span>
          <span style={{ position: "absolute", left: 4, top: 364, fontSize: 10, color: C.muted }}>CALLS</span>
          <div style={{ position: "absolute", left: 0, right: 0, top: 250, height: 1, background: C.ink }} />
          {geo.years.map((y) => (
            <span key={y} style={{ position: "absolute", left: `${geo.x(`${y}-01-01`)}%`, top: 258, fontSize: 10, color: C.muted,
              transform: "translateX(-50%)", fontFamily: "IBM Plex Mono, ui-monospace, monospace" }}>{y}</span>
          ))}
          {points.map(mark)}
        </div>
      </div>
      <aside className="w-full sm:w-64 shrink-0 rounded border p-3 text-xs" style={{ borderColor: C.line, color: C.ink }}
        data-testid="timeline-selected">
        <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: C.muted }}>Selected</div>
        {!s ? <p style={{ color: C.muted }}>Click a point for its source page.</p> : (
          <>
            <div className="font-semibold" style={{ color: C[s.kind] }}>{KIND_LABEL[s.kind]}{s.tip ? " (latest value)" : ""}</div>
            <div className="font-mono text-sm mt-1">{money(s.amount, currency)}</div>
            <div className="font-mono mt-0.5" style={{ color: C.muted }}>{s.date}</div>
            {s.why && <p className="mt-2 italic" style={{ color: C.muted }}>{s.why}</p>}
            {s.quote && <p className="mt-2 font-mono text-[11px] break-words">“{s.quote}”</p>}
            <div className="mt-2 break-words">
              {s.download_url
                ? <a href={s.download_url} target="_blank" rel="noreferrer" className="underline">{s.filename || "source page"}</a>
                : <span style={{ color: C.muted }}>{s.filename || "no source page"}</span>}
            </div>
          </>
        )}
      </aside>
    </div>
  );
}
